import React, { useState, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Label } from "@shadcn-in-nx/ui/label";
import { Loader2, Upload } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import ShareUrlCopyField from "./ShareUrlCopyField";

export default function PublishBlueprintModal({ open, onOpenChange, blueprint, onPublished }) {
    const { t } = useTranslation();
    const [changelogMessage, setChangelogMessage] = useState("");
    const [subscriberCount, setSubscriberCount] = useState(0);
    const { call: publish, isLoading } = useBluelagoon({ method: "publishBlueprint" });
    const { call: getSubscribers } = useBluelagoon({ method: "getBlueprintSubscribers" });

    useEffect(() => {
        if (open && blueprint?.id) {
            getSubscribers({ id: blueprint.id })
                .then((res) => setSubscriberCount(res?.data?.total || 0))
                .catch(() => {});
        }
    }, [open, blueprint?.id]);

    const handlePublish = async () => {
        const res = await publish({ id: blueprint.id, changelogMessage });
        const result = res?.data;
        if (result?.changed === false) {
            toast.info(t("blueprints.publish.noChanges"));
        } else if (result) {
            toast.success(t("blueprints.publish.success", { version: result.version }));
            setChangelogMessage("");
            onPublished?.(result);
            onOpenChange(false);
        }
    };

    const shareUrl = blueprint?.shareToken
        ? `${window.location.origin}/blueprints/share/${blueprint.shareToken}`
        : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Upload className="w-5 h-5" />
                        {t("blueprints.publish.title")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("blueprints.publish.description")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t("blueprints.publish.currentVersion")}</span>
                        <span className="font-medium">v{blueprint?.publishedVersion || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t("blueprints.publish.subscribers")}</span>
                        <span className="font-medium">{subscriberCount}</span>
                    </div>
                    {shareUrl && (
                        <div className="space-y-2">
                            <Label>{t("blueprints.publish.shareUrl")}</Label>
                            <ShareUrlCopyField url={shareUrl} />
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label>{t("blueprints.publish.changelog")}</Label>
                        <Textarea
                            value={changelogMessage}
                            onChange={(e) => setChangelogMessage(e.target.value)}
                            placeholder={t("blueprints.publish.changelogPlaceholder")}
                            rows={3}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t("common.cancel")}
                    </Button>
                    <Button onClick={handlePublish} disabled={isLoading}>
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {t("blueprints.publish.publishButton")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
